// Bringing a calendar tile into view is not `scrollIntoView({block:"nearest"})`:
// "nearest" stops as soon as the tile is inside the SCROLLPORT, and the
// calendar's scrollport runs underneath two pinned strips — the app header and
// the calendar's own sticky weekday row — so a tile can be "in view" and still
// be completely hidden behind them. `scrollIntoView` also bubbles into EVERY
// scrollable ancestor including the document, which is not what "scroll the
// calendar only" means.
//
// This file no longer asks the page what is covering the tile. The caller is
// Calendar, which holds refs to both its scrollport and its sticky strip and
// hands the readable BAND in. The old probe was removed rather than fixed:
// `document.elementFromPoint` returns whatever pinned thing is on top, and the
// post inspector is `position:fixed` and portaled to `document.body`
// (react-tiny-popover styles it that way and PopoverPannel stamps
// `popover-menu` on it) — so once the panel is pinned over the grid the probe
// finds the PANEL and asks for a long scroll that no vertical movement can
// ever satisfy.

/** A reveal the calendar has been asked for. `token` is what makes it a
 *  REQUEST rather than a value: paging A → B → A must run twice, and the
 *  same id repeated would look like no change at all. */
export type CalendarRevealRequest = { publicationId: string; token: number };

export const TILE_REVEAL_GAP_PX = 8;

export type TileRevealSpan = { top: number; bottom: number };
/** The band of the viewport a tile can actually be READ in: the scrollport,
 *  already inset by whatever is pinned over it. */
export type TileRevealBand = { top: number; bottom: number };
export type TileRevealPlan =
  | { kind: "visible" }
  | { kind: "scroll"; delta: number };

/**
 * How far the calendar has to move — if at all — for `tile` to be readable
 * inside `band`. `delta` is a signed VIEWPORT delta: positive scrolls the
 * content up.
 *
 * Visibility is FULL containment, deliberately not `isScrollTargetVisible`
 * (utils/scroll.ts), which is a loose overlap test where one visible pixel
 * counts as visible — a tile whose time row is under the sticky strip is not a
 * tile the user can read.
 *
 * The gap is ASYMMETRIC on purpose: it decides where a tile LANDS, never
 * whether it needed moving. Testing containment against the inset band as well
 * would scroll a tile that is already fully visible but sitting two pixels from
 * the edge, and can oscillate.
 */
export const planTileReveal = (
  tile: TileRevealSpan,
  band: TileRevealBand,
  gap = TILE_REVEAL_GAP_PX,
): TileRevealPlan => {
  if (tile.top >= band.top && tile.bottom <= band.bottom) {
    return { kind: "visible" };
  }

  const restTop = band.top + gap;
  const restBottom = band.bottom - gap;

  // A tile taller than the band it has to fit in shows its head: aligning its
  // bottom would hide the account and the time, which is the half that names
  // the post.
  if (tile.bottom - tile.top >= restBottom - restTop) {
    return { kind: "scroll", delta: tile.top - restTop };
  }
  if (tile.top < restTop) {
    return { kind: "scroll", delta: tile.top - restTop };
  }
  return { kind: "scroll", delta: tile.bottom - restBottom };
};

/**
 * Apply a plan: exactly ONE clamped scroll, on the one scroller the calendar
 * resolved. `scroller` is null when the document scrolls (the global
 * `/calendar` page, whose `main` has no height).
 *
 * One write, not scroll-then-measure-then-correct. The two-phase shape this
 * replaces could never go smooth: its correction read a pre-scroll rect, and
 * its `scrollTop =` assignment cancelled the glide it had just started.
 */
export const revealCalendarTile = (
  scroller: HTMLElement | null,
  delta: number,
  behavior: ScrollBehavior,
): void => {
  if (scroller) {
    const max = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    scroller.scrollTo({
      top: Math.max(0, Math.min(max, scroller.scrollTop + delta)),
      behavior,
    });
    return;
  }
  window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior });
};

/** Read per call, not cached at module level: the user can change it
 *  mid-session. The first reduced-motion gate in calendar code — the house
 *  form is GenerateShortsDialog's `instant` boolean. */
export const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

/** How long to wait for a smooth scroll before pulsing anyway. A glide this
 *  long has either been interrupted by the user or is not coming. */
export const SCROLL_SETTLE_TIMEOUT_MS = 900;
/** Grace for a scroll that never starts: `scrollTo` is clamped, so a delta
 *  the scrollport cannot satisfy moves nothing and no frame ever differs. */
const SCROLL_START_GRACE_MS = 120;
/** A glide writes a new offset every frame, so ONE still frame can just be a
 *  slow one. Two in a row is landed. */
const STILL_FRAMES = 2;

/**
 * Run `onSettled` once the smooth scroll issued on `scroller` has come to
 * rest — used to land the destination pulse AFTER the travel rather than
 * during it, where the bump plays on a tile that is still sliding and the eye
 * reads the two as one blurred movement.
 *
 * Frame-sampled rather than `scrollend`-driven: that event is still missing on
 * Safari, and the fallback would have to exist anyway. Returns a cancel — a
 * second jump supersedes the first, and its pulse must not land on the tile
 * the user has already left.
 */
export const onScrollSettled = (
  scroller: HTMLElement | null,
  onSettled: () => void,
): (() => void) => {
  // No frame clock — SSR, or any renderer without rAF: there is nothing to
  // wait for, so the pulse is immediate rather than lost.
  if (typeof requestAnimationFrame !== "function") {
    onSettled();
    return () => {};
  }
  const readTop = () =>
    scroller ? scroller.scrollTop : (globalThis.scrollY ?? 0);
  const startedAt = performance.now();
  let last = readTop();
  let moved = false;
  let still = 0;
  let cancelled = false;
  let frame = requestAnimationFrame(function tick() {
    if (cancelled) return;
    const top = readTop();
    if (Math.abs(top - last) < 0.5) {
      still += 1;
    } else {
      moved = true;
      still = 0;
    }
    last = top;
    const elapsed = performance.now() - startedAt;
    const settled = moved
      ? still >= STILL_FRAMES
      : elapsed >= SCROLL_START_GRACE_MS;
    if (settled || elapsed >= SCROLL_SETTLE_TIMEOUT_MS) {
      onSettled();
      return;
    }
    frame = requestAnimationFrame(tick);
  });
  return () => {
    cancelled = true;
    cancelAnimationFrame(frame);
  };
};
